/**
 * Turn a cached `card_prices.stats` object into the tier shape the ladder draws.
 *
 * Why this exists: the ladder's numbers come from Poketrace, and on a card that
 * has never been priced that means a live search plus a live fetch before a
 * single row can paint. Gengar VMAX took roughly thirty seconds that way.
 *
 * But `card_prices` is one indexed Supabase read and usually already holds the
 * same aggregates under human labels. Seeding from it paints the whole ladder
 * in one round trip, and the vendor result replaces it when it lands. Worst
 * case the seed is empty and nothing changes.
 *
 * `stats` is keyed "PSA 10" / "Near Mint"; tiers are keyed PSA_10 / NEAR_MINT.
 * The grade's decimal point is an underscore in the key, which is why 9.5
 * becomes 9_5 rather than surviving as written.
 */

const RAW_LABELS = {
  'near mint': 'NEAR_MINT',
  'lightly played': 'LIGHTLY_PLAYED',
  'moderately played': 'MODERATELY_PLAYED',
  'heavily played': 'HEAVILY_PLAYED',
  damaged: 'DAMAGED',
};

/** "PSA 9.5" -> "PSA_9_5", "Near Mint" -> "NEAR_MINT", or null if unreadable. */
export function tierKeyFor(label) {
  const clean = String(label || '').trim();
  if (!clean) return null;

  const raw = RAW_LABELS[clean.toLowerCase()];
  if (raw) return raw;

  const parts = clean.split(/\s+/);
  if (parts.length < 2) return null;

  const company = parts[0].toUpperCase();
  const grade = parts.slice(1).join(' ').replace(/\./g, '_');
  if (!/^[\d_]+$/.test(grade)) return null;

  return `${company}_${grade}`;
}

/**
 * @param {Object} stats  the `stats` column of a card_prices row
 * @returns {{ raw: Object, graded: Object } | null}
 */
export function tiersFromStats(stats) {
  if (!stats || typeof stats !== 'object') return null;

  const raw = {};
  const graded = {};
  let found = 0;

  for (const [label, v] of Object.entries(stats)) {
    const price = v?.med ?? v?.avg;
    if (price == null) continue;

    const key = tierKeyFor(label);
    if (!key) continue;

    // Named to match what Poketrace returns, so the ladder and the deep dive
    // read one shape whichever source filled it.
    const tier = {
      median30d: v.med ?? null,
      avg: v.avg ?? null,
      low: v.low ?? null,
      high: v.high ?? null,
      saleCount: v.count ?? 0,
      count: v.count ?? 0,
      lastUpdated: v.lastUpdated ?? null,
    };

    if (RAW_LABELS[label.toLowerCase()]) raw[key] = tier;
    else graded[key] = tier;
    found += 1;
  }

  return found ? { raw, graded, source: 'cache', basis: 'sold' } : null;
}

export default tiersFromStats;
